import { useState } from 'react'

import type { CustomerGroup } from '@/api/admin-customer-groups-types'
import { useCreateCustomerGroup, useUpdateCustomerGroup } from '@/api/api-mutations'
import { useGetCustomer, useListCustomers } from '@/api/react-query'
import { Badge } from '@/components/base/badge'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { toast, useCreatedToast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'
import { XMarkMini } from '@/icons/x-mark-mini'
import { useDebounce } from '@/utils/hooks'

export function CustomerGroupForm({ group }: { group?: CustomerGroup }) {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const createdToast = useCreatedToast()
	const isEdit = !!group

	const createCustomerGroup = useCreateCustomerGroup()
	const updateCustomerGroup = useUpdateCustomerGroup()

	const [customerSearch, setCustomerSearch] = useState('')
	const debouncedCustomerSearch = useDebounce(customerSearch)
	const customers = useListCustomers({ q: debouncedCustomerSearch, limit: 50 })
	const customerOptions = customers.data?.customers?.map(c => ({ value: c.uuid, label: customerLabel(c) })) ?? []

	const form = useAppForm({
		defaultValues: {
			name: group?.name ?? '',
			description: group?.description ?? '',
			externalId: group?.externalId ?? '',
			customers: group?.customerUuids ?? []
		},
		onSubmit: async ({ value }) => {
			const body = {
				name: value.name,
				// On edit, send the trimmed value (including '') so a cleared field reaches the handler and
				// is nulled; on create, omit blanks so they stay unset.
				description: isEdit ? value.description.trim() : value.description.trim() || undefined,
				externalId: isEdit ? value.externalId.trim() : value.externalId.trim() || undefined,
				customerUuids: value.customers
			}

			if (isEdit) {
				const result = await updateCustomerGroup({
					path: { customerGroupUuid: group.uuid },
					body
				})
				if (result.isSuccess) {
					toast.success(L.CustomerGroups.UpdateSuccess())
					handleSuccess()
				} else {
					toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
				}
				return
			}

			const result = await createCustomerGroup({ body })
			if (result.isSuccess) {
				createdToast(L.CustomerGroups.CreateSuccess({ name: value.name }), {
					to: '/customer-groups/$customerGroupUuid',
					params: { customerGroupUuid: result.response.uuid }
				})
				handleSuccess()
			} else {
				toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{isEdit ? L.CustomerGroups.EditTitle() : L.CustomerGroups.CreateTitle()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='name'
							validators={{
								onChange: ({ value }) => (value.trim().length === 0 ? L.General.Required() : undefined)
							}}
							children={field => (
								<field.TextField
									autoFocus
									label={L.CustomerGroups.Field.Name()}
									placeholder={L.CustomerGroups.Field.NamePlaceholder()}
								/>
							)}
						/>
						<form.AppField
							name='description'
							children={field => (
								<field.TextField
									label={L.CustomerGroups.Field.Description()}
									placeholder={L.CustomerGroups.Field.DescriptionPlaceholder()}
									optional
								/>
							)}
						/>
						<form.AppField
							name='externalId'
							children={field => (
								<field.TextField
									label={L.CustomerGroups.Field.ExternalId()}
									placeholder={L.CustomerGroups.Field.ExternalIdPlaceholder()}
									optional
								/>
							)}
						/>
					</FormFieldGroup>
					<FormFieldGroup>
						<form.AppField
							name='customers'
							children={field => (
								<field.ComboboxField
									label={L.CustomerGroups.Members()}
									placeholder={L.CustomerGroups.SelectCustomers()}
									optionsLoading={customers.isLoading}
									searchValue={customerSearch}
									onSearchValueChange={setCustomerSearch}
									options={customerOptions}
									optional
								/>
							)}
						/>
						<form.Subscribe selector={s => s.values.customers}>
							{selected =>
								selected.length > 0 && (
									<div className='flex flex-wrap gap-1'>
										{selected.map(uuid => (
											<CustomerChip
												key={uuid}
												uuid={uuid}
												onRemove={() =>
													form.setFieldValue(
														'customers',
														form.getFieldValue('customers').filter(u => u !== uuid)
													)
												}
											/>
										))}
									</div>
								)
							}
						</form.Subscribe>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					{isEdit ? <form.SaveButton /> : <form.CreateButton />}
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}

const customerLabel = (c: { firstName?: string; lastName?: string; email?: string; phone?: string; uuid: string }) =>
	[c.firstName, c.lastName].filter(Boolean).join(' ').trim() || c.email || c.phone || c.uuid

function CustomerChip({ uuid, onRemove }: { uuid: string; onRemove: () => void }) {
	const { L } = useI18n()
	const customer = useGetCustomer({ customerUuid: uuid })
	const label = customer.data ? customerLabel(customer.data) : uuid
	return (
		<Badge className='flex items-center gap-x-1'>
			{label}
			<button
				type='button'
				onClick={onRemove}
				aria-label={`${L.General.Delete()} ${label}`}
				className='text-ui-fg-muted hover:text-ui-fg-base'
			>
				<XMarkMini />
			</button>
		</Badge>
	)
}
